import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Drawer } from "@mui/material";
import { Award, FileText, MessageSquare, ShieldCheck, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { bindActionCreators } from "redux";
import { useToast } from "@/hooks/use-toast";
import { BotConversation } from "../../../models/botConversation";
import { HttpError, HttpErrors } from "../../../services/AxiosHttpService";
import { ConversationService } from "../../../services/ConversationService";
import { Dispatch, RootState } from "../../../store";
import { getConversationsAction } from "../../../store/Conversation/actions";
import { getConversations } from "../../../store/selectors";
import { BlockContainer } from "../../../styles/BlockContainer";
import { FlexContainer } from "../../../styles/FlexContainer";
import { useAuth } from "../../Reusable/AuthProvider";

export interface SideNavMenuProps {
  sideNavShow: boolean;
  conversations: BotConversation[];
  setSideNavShow: React.Dispatch<React.SetStateAction<boolean>>;
  getConversationsAction: typeof getConversationsAction;
}

function DrawerWrapper(props) {
  return (
    <React.Fragment>
      <Drawer
        sx={{ display: { xs: "block", md: "none" } }}
        anchor="left"
        open={props.open}
        onClose={props.onClose}
      >
        {props.children}
      </Drawer>
      <Box sx={{ display: { xs: "none", md: "block" } }}>{props.children}</Box>
    </React.Fragment>
  );
}

const _SideNavMenu: React.FC<SideNavMenuProps> = ({
  sideNavShow,
  conversations,
  setSideNavShow,
  getConversationsAction,
}) => {
  const navigate = useNavigate();

  const { conversationId } = useParams();
  const { user } = useAuth();
  const isUser = user?.roles?.some((role: string) => role.includes("USER"));
  const isExpert = user?.roles?.some((role: string) => role.includes("EXPERT"));
  const isAdmin = user?.roles?.some((role: string) => role.includes("ADMIN"));

  const { t } = useTranslation();
  const { toast } = useToast();
  const [conversationToDelete, setConversationToDelete] = useState<BotConversation | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!conversationToDelete) return;
    setIsDeleting(true);
    try {
      await ConversationService.deleteConversation(conversationToDelete.id);
      if (conversationToDelete.id === conversationId) {
        navigate("/feature/chat");
      }
      getConversationsAction();
    } catch (err) {
      const conflict = (err as HttpError)?.type === HttpErrors.CONFLICT;
      toast({
        title: t("SIDE_NAV_MENU_DELETE_CONVERSATION_ERROR_TITLE"),
        description: t(
          conflict
            ? "SIDE_NAV_MENU_DELETE_CONVERSATION_CERTIFIED"
            : "SIDE_NAV_MENU_DELETE_CONVERSATION_ERROR"
        ),
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setConversationToDelete(null);
    }
  };

  useEffect(() => {
    getConversationsAction();
  }, []);

  return (
    <DrawerWrapper open={sideNavShow} onClose={() => setSideNavShow(false)}>
      <BlockContainer
        sx={{
          width: sideNavShow ? "320px" : "0px",
          backgroundColor: "#111827",
          transition: "all 0.3s ease",
          overflow: "hidden",
          height: "100vh",
        }}
      >
        <BlockContainer
          sx={{
            padding: "16px",
            height: "100%",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <FlexContainer
            alignItems="center"
            justifyContent="center"
            margin="8px 0 43px 0"
          >
            <div
              style={{
                width: "95px",
                height: "95px",
                background:
                  "radial-gradient(circle, white 40%, rgba(255,255,255,0.9) 60%, rgba(60,0,95,0.3) 85%, transparent 100%)",
                borderRadius: "50%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                boxShadow:
                  "0 4px 12px rgba(0, 0, 0, 0.15), inset 0 0 20px rgba(255,255,255,0.5)",
                cursor: "pointer",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                padding: "8px",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
              onClick={() => navigate("/feature")}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.05)";
                e.currentTarget.style.boxShadow =
                  "0 6px 20px rgba(0, 0, 0, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 0, 0, 0.15)";
              }}
            >
              <img
                src="/Logo Docuveritas.png"
                alt="Docuveritas Logo"
                style={{
                  width: "40px",
                  height: "auto",
                  maxHeight: "35px",
                  objectFit: "contain",
                }}
              />

              <div
                style={{
                  fontSize: "18px",
                  lineHeight: "20px",
                  fontWeight: "bold",
                  background: "linear-gradient(45deg, #A100FF, #00D4FF)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  textAlign: "center",
                  letterSpacing: "1px",
                  fontFamily:
                    "'Segoe UI', 'Roboto', 'Inter', 'Helvetica Neue', sans-serif",
                }}
              >
                DV
              </div>
            </div>
          </FlexContainer>
          {/* Documents Button */}
          <FlexContainer
            sx={{
              alignItems: "center",
              lineHeight: "24px",
              gap: "12px",
              padding: "12px",
              color: "rgba(255, 255, 255, 0.7)",
              ":hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
            onClick={() => {
              navigate("/feature/legalDocuments");
            }}
          >
            <FileText size={20} />
            <Box component="span">{t("SIDE_NAV_MENU_MY_DOCUMENTS")}</Box>
          </FlexContainer>

          {/* Certifications Button */}
          {(isUser || isExpert) && (
            <FlexContainer
              sx={{
                alignItems: "center",
                lineHeight: "24px",
                gap: "12px",
                padding: "12px",
                color: "rgba(255, 255, 255, 0.7)",
                ":hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.3s ease",
                mt: "4px",
              }}
              onClick={() => {
                navigate("/feature/certifications");
              }}
            >
              <Award size={20} />
              <Box component="span">{t("SIDE_NAV_MENU_MY_CERTIFICATIONS")}</Box>
            </FlexContainer>
          )}

          {/* Gestion des experts (administrateurs) */}
          {isAdmin && (
            <FlexContainer
              sx={{
                alignItems: "center",
                lineHeight: "24px",
                gap: "12px",
                padding: "12px",
                color: "rgba(255, 255, 255, 0.7)",
                ":hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.3s ease",
                mt: "4px",
              }}
              onClick={() => {
                navigate("/feature/admin/experts");
              }}
            >
              <ShieldCheck size={20} />
              <Box component="span">{t("SIDE_NAV_MENU_EXPERT_MANAGEMENT")}</Box>
            </FlexContainer>
          )}

          {/* New Chat Button */}
          {isUser && (
            <>
              <Box
                component="hr"
                sx={{ borderColor: "rgba(255,255,255,0.2)", margin: "16px 0" }}
              />
              <Box
                component="button"
                sx={{
                  width: "100%",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  ":hover": { backgroundColor: "rgba(255,255,255,0.2)" },
                  color: "white",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  lineHeight: "24px",
                  gap: "12px",
                  padding: "12px",
                  transition: "all 0.3s ease",
                  border: "none", // Désactive la bordure
                  cursor: "pointer", // Change le curseur
                  outline: "none", // Supprime l'outline au focus
                }}
                onClick={() => {
                  navigate("/feature/chat");
                }}
              >
                <MessageSquare
                  size={20}
                  style={{
                    color: "rgba(255, 255, 255, 0.7)",
                    transition: "color 0.2s ease-in-out",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "rgba(255, 255, 255, 0.9)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "rgba(255, 255, 255, 0.7)")
                  }
                />
                <Box
                  component="span"
                  sx={{
                    lineHeight: "24px",
                    fontSize: "16px",
                    fontFamily:
                      'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                  }}
                >
                  {t("SIDE_NAV_MENU_NEW_CHAT")}
                </Box>
              </Box>

              {/* Chat History */}
              <BlockContainer
                sx={{
                  marginTop: "16px",
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  component="h2"
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    marginBottom: "8px",
                    padding: "0 8px",
                  }}
                >
                  {t("SIDE_NAV_MENU_RECENT_CONVERSATIONS")}
                </Box>
                <BlockContainer
                  sx={{
                    "& > * + *": { marginTop: "4px" },
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    paddingRight: "4px",
                    scrollbarWidth: "thin",
                    scrollbarColor: "rgba(255,255,255,0.25) transparent",
                  }}
                >
                  {conversations.map((chat) => {
                    const isActive = chat.id === conversationId;

                    return (
                      <BlockContainer
                        key={chat.id}
                        onClick={() => {
                          navigate(`/feature/chat/${chat.id}`);
                        }}
                        sx={{
                          position: "relative",
                          padding: "12px 40px 12px 12px",
                          backgroundColor: isActive
                            ? "rgba(255,255,255,0.1)"
                            : "transparent",
                          borderLeft: isActive
                            ? "4px solid #1976d2"
                            : "4px solid transparent", // Jaune doux
                          ":hover": {
                            backgroundColor: isActive
                              ? "rgba(255,255,255,0.15)"
                              : "rgba(255,255,255,0.05)",
                          },
                          ":hover .conversation-delete": { opacity: 1 },
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                        }}
                      >
                        <Box
                          component="button"
                          className="conversation-delete"
                          title={t("SIDE_NAV_MENU_DELETE_CONVERSATION")}
                          aria-label={t("SIDE_NAV_MENU_DELETE_CONVERSATION")}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            setConversationToDelete(chat);
                          }}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            right: "8px",
                            transform: "translateY(-50%)",
                            opacity: isActive ? 1 : 0,
                            background: "none",
                            border: "none",
                            padding: "6px",
                            borderRadius: "6px",
                            color: "rgba(255,255,255,0.6)",
                            cursor: "pointer",
                            display: "flex",
                            transition: "opacity 0.2s ease, color 0.2s ease",
                            ":hover": { color: "#f87171", backgroundColor: "rgba(255,255,255,0.08)" },
                            ":focus-visible": { opacity: 1 },
                          }}
                        >
                          <Trash2 size={16} />
                        </Box>
                        <Box
                          component="h3"
                          sx={{
                            margin: 0,
                            color: "white",
                            fontWeight: isActive ? "600" : "500",
                            lineHeight: "24px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "16px",
                          }}
                        >
                          {chat.title}
                        </Box>
                        <Box
                          component="p"
                          sx={{
                            color: "#9CA3AF",
                            fontSize: "0.875rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            margin: "0",
                            lineHeight: "20px",
                          }}
                        >
                          {chat.messages[0]?.content || ""}
                        </Box>
                      </BlockContainer>
                    );
                  })}
                </BlockContainer>
              </BlockContainer>
            </>
          )}
        </BlockContainer>
      </BlockContainer>
      <Dialog
        open={conversationToDelete !== null}
        onClose={() => !isDeleting && setConversationToDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("SIDE_NAV_MENU_DELETE_CONVERSATION_TITLE")}</DialogTitle>
        <DialogContent>
          {t("SIDE_NAV_MENU_DELETE_CONVERSATION_CONFIRM", {
            title: conversationToDelete?.title || "",
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConversationToDelete(null)} disabled={isDeleting}>
            {t("SIDE_NAV_MENU_DELETE_CONVERSATION_CANCEL")}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={isDeleting}>
            {t("SIDE_NAV_MENU_DELETE_CONVERSATION_ACTION")}
          </Button>
        </DialogActions>
      </Dialog>
    </DrawerWrapper>
  );
};

export const SideNavMenu = connect(
  (state: RootState) => ({
    conversations: getConversations(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        getConversationsAction: getConversationsAction,
      },
      dispatch
    )
)(_SideNavMenu);

export default SideNavMenu;
